<template>
  <div class="smart-art-preview">
    <svg
      class="smart-art-canvas"
      :viewBox="`0 0 ${canvasWidth} ${canvasHeight}`"
      role="img"
      :aria-label="`${data.title || 'SmartArt'}结构预览`"
    >
      <defs>
        <linearGradient id="smart-art-root" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#10a96f"></stop>
          <stop offset="100%" stop-color="#39c994"></stop>
        </linearGradient>
        <filter id="smart-art-shadow" x="-20%" y="-30%" width="140%" height="170%">
          <feDropShadow dx="0" dy="5" stdDeviation="6" flood-color="#1c5844" flood-opacity=".14"></feDropShadow>
        </filter>
      </defs>

      <g class="smart-art-edges">
        <path
          v-for="edge in layoutEdges"
          :key="`${edge.sourceId}-${edge.targetId}`"
          :d="edgePath(edge)"
        ></path>
      </g>

      <g
        v-for="node in layoutNodes"
        :key="node.id"
        class="smart-art-node"
        :class="`level-${Math.min(node.depth, 2)}`"
        :transform="`translate(${node.x}, ${node.y})`"
      >
        <rect
          :x="-node.width / 2"
          :y="-node.height / 2"
          :width="node.width"
          :height="node.height"
          :rx="node.depth === 0 ? 17 : 13"
        ></rect>
        <circle v-if="node.depth > 0" :cx="-node.width / 2 + 20" cy="0" r="12"></circle>
        <text :x="node.depth > 0 ? 9 : 0" text-anchor="middle">
          <tspan
            v-for="(line, index) in nodeLines(node)"
            :key="`${node.id}-${index}`"
            :x="node.depth > 0 ? 9 : 0"
            :dy="index === 0 ? `${-(nodeLines(node).length - 1) * 9}px` : '18px'"
          >{{ line }}</tspan>
        </text>
      </g>
    </svg>
    <p class="smart-art-hint">根据 SmartArt 节点与关系生成结构预览</p>
  </div>
</template>

<script>
export default {
  name: 'SmartArtPreview',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    normalizedNodes () {
      const source = Array.isArray(this.data.nodes) ? this.data.nodes : []
      return source
        .map((node, index) => ({
          id: String((node && (node.id || node.modelId)) || `smart-art-node-${index + 1}`),
          text: String((node && (node.text || node.label || node.title)) || '').trim(),
          type: String((node && node.type) || '')
        }))
        .filter(node => node.text)
        .slice(0, 30)
    },
    normalizedEdges () {
      const nodeIds = new Set(this.normalizedNodes.map(node => node.id))
      const source = Array.isArray(this.data.edges) ? this.data.edges : []
      return source
        .map(edge => ({
          sourceId: String((edge && (edge.sourceId || edge.source || edge.from)) || ''),
          targetId: String((edge && (edge.targetId || edge.target || edge.to)) || ''),
          type: String((edge && edge.type) || '')
        }))
        .filter(edge => edge.sourceId !== edge.targetId && nodeIds.has(edge.sourceId) && nodeIds.has(edge.targetId))
    },
    layout () {
      if (!this.normalizedNodes.length) return { nodes: [], edges: [], height: 360 }

      const incoming = new Map(this.normalizedNodes.map(node => [node.id, 0]))
      const outgoing = new Map(this.normalizedNodes.map(node => [node.id, []]))
      this.normalizedEdges.forEach(edge => {
        incoming.set(edge.targetId, (incoming.get(edge.targetId) || 0) + 1)
        outgoing.get(edge.sourceId).push(edge.targetId)
      })

      const firstId = this.normalizedNodes[0].id
      const roots = this.normalizedNodes.filter(node => !incoming.get(node.id)).map(node => node.id)
      const queue = (roots.length ? roots : [firstId]).map(id => ({ id, depth: 0 }))
      const depths = new Map()
      const treeEdges = []
      while (queue.length) {
        const current = queue.shift()
        if (depths.has(current.id)) continue
        depths.set(current.id, current.depth)
        ;(outgoing.get(current.id) || []).forEach(targetId => {
          if (!depths.has(targetId)) {
            treeEdges.push({ sourceId: current.id, targetId })
            queue.push({ id: targetId, depth: current.depth + 1 })
          }
        })
      }

      this.normalizedNodes.forEach(node => {
        if (depths.has(node.id)) return
        depths.set(node.id, node.id === firstId ? 0 : 1)
        if (node.id !== firstId) treeEdges.push({ sourceId: firstId, targetId: node.id })
      })

      const levels = new Map()
      this.normalizedNodes.forEach(node => {
        const depth = depths.get(node.id) || 0
        if (!levels.has(depth)) levels.set(depth, [])
        levels.get(depth).push(node)
      })

      const positioned = []
      let row = 0
      Array.from(levels.keys()).sort((a, b) => a - b).forEach(depth => {
        const levelNodes = levels.get(depth)
        const maxPerRow = depth === 0 ? 2 : 3
        for (let offset = 0; offset < levelNodes.length; offset += maxPerRow) {
          const chunk = levelNodes.slice(offset, offset + maxPerRow)
          chunk.forEach((node, index) => {
            const width = depth === 0 ? 220 : 174
            positioned.push({
              ...node,
              depth,
              x: this.canvasWidth * (index + 1) / (chunk.length + 1),
              y: 72 + row * 132,
              width,
              height: depth === 0 ? 70 : 64
            })
          })
          row += 1
        }
      })

      const positionedIds = new Set(positioned.map(node => node.id))
      const usableEdges = (treeEdges.length ? treeEdges : this.normalizedEdges)
        .filter(edge => positionedIds.has(edge.sourceId) && positionedIds.has(edge.targetId))
      return { nodes: positioned, edges: usableEdges, height: Math.max(360, 130 + row * 132) }
    },
    layoutNodes () {
      return this.layout.nodes
    },
    layoutEdges () {
      return this.layout.edges
    },
    canvasWidth () {
      return 660
    },
    canvasHeight () {
      return this.layout.height
    }
  },
  methods: {
    nodeById (id) {
      return this.layoutNodes.find(node => node.id === id)
    },
    edgePath (edge) {
      const source = this.nodeById(edge.sourceId)
      const target = this.nodeById(edge.targetId)
      if (!source || !target) return ''
      const startY = source.y + source.height / 2
      const endY = target.y - target.height / 2
      const middleY = startY + (endY - startY) / 2
      return `M ${source.x} ${startY} V ${middleY} H ${target.x} V ${endY}`
    },
    nodeLines (node) {
      const text = node.text
      if (text.length <= 10) return [text]
      return [text.slice(0, 10), text.slice(10, 20)]
    }
  }
}
</script>

<style scoped>
.smart-art-preview {
  position: relative;
  margin-top: 13px;
  overflow: hidden;
  border: 1px solid #e2ebe7;
  border-radius: 9px;
  background:
    radial-gradient(circle at 12% 15%, rgba(61, 206, 157, .11), transparent 26%),
    linear-gradient(145deg, #fbfffd 0%, #f3f8fb 100%);
}

.smart-art-canvas {
  display: block;
  width: 100%;
  min-height: 330px;
}

.smart-art-edges path {
  fill: none;
  stroke: #a5b8b0;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.smart-art-node rect {
  fill: #fff;
  stroke: #c7ddd5;
  stroke-width: 2;
  filter: url(#smart-art-shadow);
}

.smart-art-node.level-0 rect {
  fill: url(#smart-art-root);
  stroke: #0e9d68;
}

.smart-art-node.level-1 rect {
  fill: #fff;
  stroke: #37bc8b;
}

.smart-art-node.level-2 rect {
  fill: #f9fcfb;
}

.smart-art-node circle {
  fill: #e3f7ef;
  stroke: #61c9a1;
  stroke-width: 2;
}

.smart-art-node text {
  fill: #334941;
  font-size: 15px;
  font-weight: 600;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
  dominant-baseline: middle;
}

.smart-art-node.level-0 text {
  fill: #fff;
  font-size: 17px;
  font-weight: 700;
}

.smart-art-hint {
  margin: -4px 12px 10px;
  color: #94a19c;
  font-size: 11px;
  text-align: right;
}
</style>
